import { Card } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import useGetKitchenOrders from "../../apis/useGetKitchenOrders";
import {
	IKitchenOrderCustomization,
	IKitchenOrderSelection,
	IOrdersTaxEntry,
} from "../../apis/useGetKitchenOrders/types";
import {
	centsToDollarsDisplay,
	displayCentsWithDollarSign,
} from "../../common/helpers/stringUtils";
import { GST_REGEX, SERVICE_CHARGE_REGEX } from "../../common/helpers/taxHelper";
import Condition from "../../components/Conditional/Condition";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { PATH } from "../../router/path";
import "../PaymentPage/PaymentDetail/PaymentDetail.scss";
import "./KitchenOrderSentPage.scss";

interface IMergedItem {
	key: string;
	orderId: string;
	name: string;
	quantity: number;
	gross_value: number;
	customizations: IKitchenOrderCustomization[];
	selections: IKitchenOrderSelection[];
}

const findOrdersTaxEntry = (
	orderTax: IOrdersTaxEntry[] | undefined,
	regex: RegExp
): IOrdersTaxEntry | undefined =>
	orderTax?.find((entry) => regex.test(entry.description ?? "") || regex.test(entry.type ?? ""));

const renderItemRow = (item: IMergedItem, isVoided: boolean, showOrderBoundary = false) => (
	<li
		key={item.key}
		className={[
			"CartItem",
			isVoided ? "CartItem--voided" : "",
			showOrderBoundary ? "CartItem--orderBoundary" : "",
		]
			.filter(Boolean)
			.join(" ")}
	>
		<div className="ItemHeader">
			<div className="ItemName">
				<span className="ItemNameText">{item.name}</span>
			</div>
			<div className="ItemHeaderRight">
				<span className="ItemQuantity">{item.quantity}</span>
				<span className="Price TabularNums">{`$${centsToDollarsDisplay(item.gross_value)}`}</span>
			</div>
		</div>
		<Condition check={item.customizations.length > 0 || item.selections.length > 0}>
			<div className="ItemContents">
				<div className="CartItemCustomizations">
					<ul className="Options">
						{item.customizations.map((c) => (
							<li key={`c-${c.food_supplier_customization_option_id}`} className="Option">
								<span className="OptionTitle">{c.title_en}</span>
							</li>
						))}
						{item.selections.map((s, idx) => (
							<li key={`s-${s.food_supplier_menu_item_id ?? idx}-${idx}`} className="Option">
								<span className="OptionTitle">{s.title_en}</span>
							</li>
						))}
					</ul>
				</div>
			</div>
		</Condition>
	</li>
);

const KitchenOrderSentPage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { orders, isLoading } = useGetKitchenOrders();
	const { state: tableNumber } = useConfigContext((state) => state.table);

	const { activeItems, voidedItems } = useMemo((): {
		activeItems: IMergedItem[];
		voidedItems: IMergedItem[];
	} => {
		const active: IMergedItem[] = [];
		const voided: IMergedItem[] = [];

		for (const order of orders) {
			const orderVoided = order.attributes.order?.status === "VOIDED";
			const items = order.attributes.order?.orders_items ?? [];

			for (const item of items) {
				const itemVoided = orderVoided || item.status === "VOIDED";
				const row: IMergedItem = {
					key: `${itemVoided ? "voided" : "active"}-${order.id}-${item.id}`,
					orderId: order.id,
					name: item.name ?? "-",
					quantity: Number(item.quantity ?? 1),
					gross_value: item.gross_value ?? 0,
					customizations: item.customizations ?? [],
					selections: item.selections ?? [],
				};

				(itemVoided ? voided : active).push(row);
			}
		}

		return { activeItems: active, voidedItems: voided };
	}, [orders]);

	// Order-level VOID is excluded from totals; BE already recalculates orders_figures /
	// orders_tax on partial item void, so item-level VOIDED needs no further adjustment here.
	const activeOrders = useMemo(
		() => orders.filter((order) => order.attributes.order?.status !== "VOIDED"),
		[orders]
	);

	const subtotal = useMemo(() => {
		return activeOrders.reduce((sum, order) => {
			return sum + (order.attributes.order?.orders_figures?.gross_value ?? 0);
		}, 0);
	}, [activeOrders]);

	const total = useMemo(() => {
		return activeOrders.reduce((sum, order) => {
			const figures = order.attributes.order?.orders_figures;

			return sum + (figures?.net_value ?? figures?.gross_value ?? 0);
		}, 0);
	}, [activeOrders]);

	const takeawayfee = useMemo(() => {
		return activeOrders.reduce((sum, order) => {
			const figures = order.attributes.order?.orders_figures;

			return sum + (figures?.takeaway_fee ?? 0);
		}, 0);
	}, [activeOrders]);

	const aggregateTax = (regex: RegExp): { name: string; amount: number } | undefined => {
		let firstEntry;
		let totalAmount = 0;

		for (const order of activeOrders) {
			const entry = findOrdersTaxEntry(order.attributes.order?.orders_tax, regex);

			if (entry) {
				firstEntry ??= entry;
				totalAmount += entry.amount;
			}
		}

		return firstEntry ? { name: firstEntry.description, amount: totalAmount } : undefined;
	};

	const serviceCharge = useMemo(() => aggregateTax(SERVICE_CHARGE_REGEX), [activeOrders]);
	const gst = useMemo(() => aggregateTax(GST_REGEX), [activeOrders]);

	const hasAnyRow = activeItems.length > 0 || voidedItems.length > 0;
	const activeItemCount = useMemo(
		() => activeItems.reduce((sum, item) => sum + item.quantity, 0),
		[activeItems]
	);

	return (
		<div className="KitchenOrderSentPage">
			<div className="KitchenOrderSentHeader">
				<h1 className="KitchenOrderSentTitle">{t("kitchenOrderSent.title")}</h1>
				<Condition check={!isLoading && activeItems.length > 0}>
					<div className="KitchenOrderSentSubtitle">
						<Condition check={!!tableNumber}>
							<span className="SubtitleSegment">{`${t("kitchenOrderSent.subtitleTable")} ${tableNumber ?? ""}`}</span>
						</Condition>
						<span className="SubtitleSegment">
							{t("kitchenOrderSent.subtitleItems", { count: activeItemCount })}
						</span>
						<span className="SubtitleSegment TabularNums">{displayCentsWithDollarSign(total)}</span>
					</div>
				</Condition>
			</div>

			<div className="KitchenOrderSentBody">
				<Condition check={isLoading}>
					<div className="BillDetailsLoading">
						<CircularProgress size={24} />
					</div>
				</Condition>
				<Condition check={!isLoading && hasAnyRow}>
					<Card className="PaymentDetail">
						<div className="PaymentDetailSection">
							<ul className="CartContainer">
								{activeItems.map((item, idx) => {
									const next = activeItems[idx + 1];
									const showOrderBoundary = !!next && next.orderId !== item.orderId;

									return renderItemRow(item, false, showOrderBoundary);
								})}
								<Condition check={voidedItems.length > 0}>
									<li className="VoidedSectionHeader">
										<span className="VoidedSectionLabel">
											<span className="VoidedSectionHeaderText">
												{`${t("billPage.cancelledSection")} (${voidedItems.length})`}
											</span>
										</span>
									</li>
								</Condition>
								{voidedItems.map((item) => renderItemRow(item, true))}
							</ul>
							<div className="BillSummary">
								<Condition check={takeawayfee > 0}>
									<div className="PaymentDetailSubtotal">
										<div className="SummaryRow">
											<span className="SummaryLabel">{t("orderConfirmation.takeawayfee")}</span>
											<span className="SummaryValue">
												{displayCentsWithDollarSign(takeawayfee)}
											</span>
										</div>
									</div>
								</Condition>

								<div className="PaymentDetailDivider" />
								<div className="PaymentDetailSubtotal">
									<div className="SummaryRow">
										<span className="SummaryLabel">{t("orderConfirmation.subtotal")}</span>
										<span className="SummaryValue">{displayCentsWithDollarSign(subtotal)}</span>
									</div>
									<Condition check={!!serviceCharge}>
										<div className="SummaryRow ServiceChargeRow">
											<span className="SummaryLabel">{serviceCharge?.name}</span>
											<span className="SummaryValue">
												{displayCentsWithDollarSign(serviceCharge?.amount ?? 0)}
											</span>
										</div>
									</Condition>
									<Condition check={!!gst}>
										<div className="SummaryRow GstRow">
											<span className="SummaryLabel">{gst?.name}</span>
											<span className="SummaryValue">
												{displayCentsWithDollarSign(gst?.amount ?? 0)}
											</span>
										</div>
									</Condition>
									<div className="SummaryRow FinalTotalRow">
										<span className="SummaryLabel">{t("billPage.totalAmount")}</span>
										<span className="SummaryValue">{displayCentsWithDollarSign(total)}</span>
									</div>
								</div>
							</div>
						</div>
					</Card>
				</Condition>
			</div>

			<button
				className="CheckoutButton"
				aria-label={t("kitchenOrderSent.addMoreItems")}
				onClick={() => navigate(PATH.PRODUCT_LIST)}
			>
				<div className="CheckoutButtonLabel">{t("kitchenOrderSent.addMoreItems")}</div>
			</button>
		</div>
	);
};

export default KitchenOrderSentPage;
